import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown, Users, Radio, RefreshCw, Eye, AlertCircle, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    Legend, AreaChart, Area, ComposedChart,
} from "recharts";

const TT_STYLE = {
    contentStyle: {
        background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
        borderRadius: 10, fontSize: 12, padding: "8px 12px",
        boxShadow: "0 8px 32px rgba(0,0,0,.25)",
    },
};
const TICK = { fill: "hsl(var(--muted-foreground))", fontSize: 10 };
const GRID = { strokeOpacity: 0.06, vertical: false, strokeDasharray: "4 4" };
const COLORS = ["hsl(188 92% 45%)","hsl(158 64% 45%)","hsl(38 92% 55%)","hsl(280 60% 55%)","hsl(0 72% 52%)","hsl(210 70% 55%)"];
const MAX_DAYS = 15;
const CHART_H = "h-[240px]";

function trimDays(data) {
    if (!data || data.length <= MAX_DAYS) return data;
    return data.slice(-MAX_DAYS);
}

function parseGraph(graph) {
    if (!graph?.columns || graph.columns.length < 2) return { data: [], keys: [] };
    const xCol = graph.columns[0];
    const series = graph.columns.slice(1);
    const names = graph.names || {};
    const keys = series.map(col => names[col[0]] || col[0]);
    const data = xCol.slice(1).map((x, i) => {
        const pt = { ts: x, date: new Date(x).toLocaleDateString("ar-EG", { month: "short", day: "numeric" }) };
        series.forEach(col => { pt[names[col[0]] || col[0]] = col[i + 1]; });
        return pt;
    });
    return { data: trimDays(data), keys };
}

/* ━━━━ Single Channel Card — fetches its OWN stats ━━━━ */
function ChannelCard({ channel, delay }) {
    const [expanded, setExpanded] = useState(true);
    const [tg, setTg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const r = await api.get(`/stats/channel/${channel.chat_id}/telegram-stats`, { timeout: 30000 });
            setTg(r.data);
        } catch {
            setFailed(true);
            setTg(null);
        }
        setLoading(false);
    }, [channel.chat_id]);

    useEffect(() => {
        const timer = setTimeout(fetchStats, delay);
        return () => clearTimeout(timer);
    }, [fetchStats, delay]);

    const hasData = tg && !tg.error && Object.keys(tg).length > 0;
    const graphs = tg?.graphs || {};
    const summary = tg?.summary || {};
    const followers = summary.followers || {};
    const viewsPerPost = summary.views_per_post || {};
    const delta = (followers.current || 0) - (followers.previous || 0);
    const pct = followers.previous ? ((delta / followers.previous) * 100).toFixed(1) : 0;

    const growth = parseGraph(graphs.growth_graph);
    const followersJL = parseGraph(graphs.followers_graph);
    const newBySource = parseGraph(graphs.new_followers_by_source_graph);
    const viewsBySource = parseGraph(graphs.views_by_source_graph);
    const isGroup = channel.chat_type !== "channel";

    return (
        <Card className="bg-card/60 backdrop-blur border-border/40 overflow-hidden">
            <CardHeader className="pb-3 border-b border-border/30 cursor-pointer hover:bg-card/80 transition-colors" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${isGroup ? "bg-violet-500/10 border-violet-500/20" : "bg-cyan-500/10 border-cyan-500/20"}`}>
                            {isGroup ? <Users className="h-5 w-5 text-violet-400" /> : <Radio className="h-5 w-5 text-cyan-400" />}
                        </div>
                        <div>
                            <CardTitle className="text-lg leading-tight">
                                {channel.username ? (
                                    <a href={`https://t.me/${channel.username}`} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors" onClick={e => e.stopPropagation()}>{channel.title}</a>
                                ) : channel.title}
                            </CardTitle>
                            <div className="flex items-center gap-2 mt-0.5">
                                {channel.username && <span className="text-xs text-muted-foreground font-mono" dir="ltr">@{channel.username}</span>}
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isGroup ? "border-violet-500/20 text-violet-300" : "border-cyan-500/20 text-cyan-300"}`}>{isGroup ? "مجموعة" : "قناة"}</Badge>
                                {loading && <Badge variant="outline" className="text-[10px] border-cyan-500/20 text-cyan-300 gap-1"><Loader2 className="h-3 w-3 animate-spin" />تحميل</Badge>}
                                {hasData && <Badge variant="outline" className="text-[10px] border-emerald-500/20 text-emerald-300">✓</Badge>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-left">
                            <div className="text-2xl font-bold tabular-nums" dir="ltr">{(channel.member_count || 0).toLocaleString("en")}</div>
                            {delta !== 0 && (
                                <div className={`flex items-center gap-1 text-xs font-medium justify-end ${delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                    {delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                    <span dir="ltr">{delta > 0 ? "+" : ""}{delta.toLocaleString("en")} ({pct}%)</span>
                                </div>
                            )}
                        </div>
                        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </div>
                </div>
                {hasData && (followers.current || 0) > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        <Badge variant="outline" className="gap-1.5 text-xs border-border/40"><Users className="h-3 w-3 text-cyan-400" /><span dir="ltr">{(followers.current || 0).toLocaleString("en")}</span> مشترك</Badge>
                        <Badge variant="outline" className="gap-1.5 text-xs border-border/40"><Eye className="h-3 w-3 text-amber-400" /><span dir="ltr">{(viewsPerPost.current || 0).toLocaleString("en")}</span> مشاهدة/منشور</Badge>
                    </div>
                )}
            </CardHeader>

            {expanded && (
                <CardContent className="pt-4">
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> جاري التحميل...
                        </div>
                    ) : failed ? (
                        <div className="flex items-center justify-between py-3">
                            <span className="flex items-center gap-2 text-xs text-muted-foreground"><AlertCircle className="h-3.5 w-3.5" /> فشل التحميل</span>
                            <Button variant="ghost" size="sm" onClick={fetchStats} className="gap-1 text-xs h-7"><RefreshCw className="h-3 w-3" /> إعادة</Button>
                        </div>
                    ) : !hasData ? (
                        <div className="flex items-center justify-between py-3">
                            <span className="flex items-center gap-2 text-xs text-muted-foreground"><AlertCircle className="h-3.5 w-3.5" /> {tg?.error || "غير متوفرة"}</span>
                            <Button variant="ghost" size="sm" onClick={fetchStats} className="gap-1 text-xs h-7"><RefreshCw className="h-3 w-3" /> إعادة</Button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {growth.data.length > 0 && growth.keys.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-cyan-400" /> النمو <span className="text-[10px] text-muted-foreground font-normal">آخر {growth.data.length} يوم</span></h3>
                                    <div className={CHART_H} dir="ltr"><ResponsiveContainer width="100%" height="100%"><AreaChart data={growth.data}><defs><linearGradient id={`gr-${channel.chat_id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(188 92% 45%)" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(188 92% 45%)" stopOpacity={0} /></linearGradient></defs><CartesianGrid {...GRID} /><XAxis dataKey="date" tick={TICK} interval={Math.max(0, Math.floor(growth.data.length / 6))} /><YAxis tick={TICK} domain={["dataMin - 50","dataMax + 50"]} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}K` : v} width={50} /><Tooltip {...TT_STYLE} />{growth.keys.map(k => (<Area key={k} type="monotone" dataKey={k} stroke="hsl(188 92% 45%)" strokeWidth={2.5} fill={`url(#gr-${channel.chat_id})`} name={k} />))}</AreaChart></ResponsiveContainer></div>
                                </div>
                            )}
                            {newBySource.data.length > 0 && newBySource.keys.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-emerald-400" /> المشتركون الجدد حسب المصدر</h3>
                                    <div className={CHART_H} dir="ltr"><ResponsiveContainer width="100%" height="100%"><BarChart data={newBySource.data} barCategoryGap="20%"><CartesianGrid {...GRID} /><XAxis dataKey="date" tick={TICK} interval={Math.max(0, Math.floor(newBySource.data.length / 6))} /><YAxis tick={TICK} width={40} /><Tooltip {...TT_STYLE} /><Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />{newBySource.keys.map((key, i) => (<Bar key={key} dataKey={key} stackId="a" fill={COLORS[i % COLORS.length]} name={key} radius={i === newBySource.keys.length - 1 ? [4,4,0,0] : [0,0,0,0]} />))}</BarChart></ResponsiveContainer></div>
                                </div>
                            )}
                            {viewsBySource.data.length > 0 && viewsBySource.keys.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Eye className="h-4 w-4 text-amber-400" /> المشاهدات حسب المصدر</h3>
                                    <div className={CHART_H} dir="ltr"><ResponsiveContainer width="100%" height="100%"><BarChart data={viewsBySource.data} barCategoryGap="20%"><CartesianGrid {...GRID} /><XAxis dataKey="date" tick={TICK} interval={Math.max(0, Math.floor(viewsBySource.data.length / 6))} /><YAxis tick={TICK} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}K` : v} width={50} /><Tooltip {...TT_STYLE} /><Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />{viewsBySource.keys.map((key, i) => (<Bar key={key} dataKey={key} stackId="a" fill={COLORS[i % COLORS.length]} name={key} radius={i === viewsBySource.keys.length - 1 ? [4,4,0,0] : [0,0,0,0]} />))}</BarChart></ResponsiveContainer></div>
                                </div>
                            )}
                            {followersJL.data.length > 0 && followersJL.keys.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium mb-3">📊 الانضمام والمغادرة</h3>
                                    <div className={CHART_H} dir="ltr"><ResponsiveContainer width="100%" height="100%"><ComposedChart data={followersJL.data} barCategoryGap="25%"><CartesianGrid {...GRID} /><XAxis dataKey="date" tick={TICK} interval={Math.max(0, Math.floor(followersJL.data.length / 6))} /><YAxis tick={TICK} width={40} /><Tooltip {...TT_STYLE} /><Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />{followersJL.keys.map((key, i) => (<Bar key={key} dataKey={key} fill={i === 0 ? "hsl(158 64% 45%)" : "hsl(0 72% 52%)"} radius={[4,4,0,0]} name={key === "Joined" ? "انضموا" : key === "Left" ? "غادروا" : key} />))}</ComposedChart></ResponsiveContainer></div>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
}

/* ━━━━ Main Page ━━━━ */
export default function Analytics() {
    const [channels, setChannels] = useState([]);
    const [sortBy, setSortBy] = useState("members");
    const [loading, setLoading] = useState(true);
    const [key, setKey] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const ch = await api.get("/stats/channels-overview");
            setChannels(ch.data.channels || []);
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const refresh = () => { setKey(k => k + 1); load(); };

    const sorted = [...channels].sort((a, b) => {
        if (sortBy === "members") return (b.member_count || 0) - (a.member_count || 0);
        if (sortBy === "members_asc") return (a.member_count || 0) - (b.member_count || 0);
        if (sortBy === "name") return (a.title || "").localeCompare(b.title || "");
        return 0;
    });

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">إحصائيات القنوات والمجموعات</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        بيانات تيليجرام الرسمية — آخر {MAX_DAYS} يوم • {channels.length} قناة/مجموعة
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="h-9 rounded-md border border-border bg-card px-3 text-sm">
                        <option value="members">الأكبر أولاً</option>
                        <option value="members_asc">الأصغر أولاً</option>
                        <option value="name">الاسم</option>
                    </select>
                    <Button variant="outline" size="sm" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> تحديث</Button>
                </div>
            </div>
            {loading ? (
                <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-[120px] rounded-xl" />)}</div>
            ) : sorted.length === 0 ? (
                <Card className="bg-card/60"><CardContent className="py-16 text-center text-muted-foreground"><Radio className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>لا توجد قنوات أو مجموعات</p></CardContent></Card>
            ) : (
                <div className="space-y-4">
                    {sorted.map((ch, i) => (
                        <ChannelCard key={`${ch.chat_id}-${key}`} channel={ch} delay={Math.floor(i / 3) * 5000} />
                    ))}
                </div>
            )}
        </div>
    );
}
